// components/sections/ShopByBrand.jsx
import { Link } from "react-router-dom";

import { Image, TitleSection } from "common";
import { buildBrandPath } from "utils/productRoutes";

import BlueBanner from "./BlueBanner";

import { URLS } from "@/constants/constant";

/**
 * Each key indexes `URLS.BRANDS` for the logo image, so it can't change. What it is **not**
 * is a valid brand for `/products/by-brand/`: that route matches the item's brand columns
 * by name, and a dash-joined slug matches neither. Measured against the live API,
 * `johnson-and-johnson` and `surgical-specialties` both returned **0 products** — the other
 * 13 survived only because a single-word key happens to equal the brand name.
 *
 * So a key that isn't the brand name carries the name explicitly. `buildBrandPath` encodes
 * it, which is what makes `Johnson & Johnson` routable.
 */
const brandTiles = [
  // The logo key and the brand name genuinely differ here: `URLS.BRANDS` has no "d2" —
  // the asset key is "d2-healthcare" — so the bare `{ key: "d2" }` this used to be
  // rendered an `undefined` src (masked by `Image`'s generic fallback) all along.
  { key: "d2-healthcare", brand: "D2" },
  { key: "3m" },
  { key: "aurelia" },
  { key: "dmg" },
  { key: "kerr" },
  { key: "keystone" },
  { key: "microcopy" },
  { key: "johnson-and-johnson", brand: "Johnson & Johnson" },
  { key: "dentsply" },
  { key: "diadent" },
  { key: "medicom" },
  { key: "premier" },
  { key: "surgical-specialties", brand: "Surgical Specialties" },
  { key: "flight" },
  { key: "mark3" },
];

const brands = brandTiles.map(({ key, brand }) => ({
  key,
  url: URLS.BRANDS[key],
  name: key.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
  path: buildBrandPath(brand || key),
}));

export default function ShopByBrand() {
  return (
    <>
      <TitleSection
        title="Shop By Brands"
        subtitle="Trusted quality from leading manufacturers"
        itemCount={brands.length}
        itemLabel="brands"
        colorScheme="purple"
        icon={
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 713.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 410 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 410-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 713.138-3.138z"
            />
          </svg>
        }
      />
      <BlueBanner
        items={brands}
        columns={{ base: 3, md: 4, lg: 6 }}
        enableHorizontalScroll={false}
        renderItem={({ url, name, path }) => (
          <Link to={path}>
            <Image
              src={url}
              alt={name}
              className="h-12 object-contain hover:scale-105 transition-transform"
            />
          </Link>
        )}
      />
    </>
  );
}
